import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { GahGroups } from '@data/schema/gah-groups';
import { RuntimeEnvironmentService } from '@gah-core/services/runtime-environment.service';
import { UserStoreService } from '@gah-core/services/user-store.service';

@Injectable({
  providedIn: 'root'
})
export class GoogleAnalyticsService {
  // private browserGlobals = {
  //   windowRef(): any {
  //     return window;
  //   },
  //   documentRef(): any {
  //     return document;
  //   },
  // };

  private window: Window | null;
  private isGtmLoaded = false;
  private isNoScriptAdded = false;
  private renderer: Renderer2;

  // GTM ID validation pattern - matches GTM-XXXXXXXX format
  private readonly GTM_ID_PATTERN = /^GTM-[A-Z0-9]{6,10}$/;
  
  // GTM URLs - retrieved from environment configuration
  private get gtmScriptUrl(): string {
    return this.runtimeEnvService.gtmScriptUrl;
  }
  
  private get gtmNoScriptUrl(): string {
    return this.runtimeEnvService.gtmNoScriptUrl;
  }

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private userStoreService: UserStoreService,
    private runtimeEnvService: RuntimeEnvironmentService,
    private rendererFactory: RendererFactory2
  ) {
    this.window = this.document.defaultView;
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  /**
   * Validates GTM ID format to prevent XSS attacks
   * Valid format: GTM-XXXXXXXX (alphanumeric, 6-10 characters after prefix)
   */
  private isValidGtmId(gtmId: string | null | undefined): gtmId is string {
    if (!gtmId || typeof gtmId !== 'string') {
      return false;
    }
    return this.GTM_ID_PATTERN.test(gtmId);
  }

  /**
   * Initialize Google Tag Manager dynamically based on environment
   * Matches the standard GTM script pattern
   */
  initializeGTM(): void {
    if (this.isGtmLoaded) {
      console.warn('GTM already initialized');
      return;
    }

    const gtmId = this.runtimeEnvService.gtmId;

    if (!gtmId) {
      console.warn(
        `GTM ID not configured for ${this.runtimeEnvService.environmentName} environment`
      );
      return;
    }

    // Validate GTM ID format to prevent XSS
    if (!this.isValidGtmId(gtmId)) {
      console.error('Invalid GTM ID format. Expected format: GTM-XXXXXXXX');
      return;
    }

    console.log(
      `Initializing GTM for ${this.runtimeEnvService.environmentName} environment with ID: ${gtmId}`
    );

    if (this.window) {
      const win = this.window as any;
      const doc = this.document;
      const dataLayerName = 'dataLayer';

      // Initialize dataLayer array
      win[dataLayerName] = win[dataLayerName] || [];
      
      // Define gtag helper function that pushes to dataLayer
      win['gtag'] = function(...args: any[]) {
        win[dataLayerName].push(args);
      };

      // Push GTM start event to dataLayer
      win[dataLayerName].push({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js'
      });

      // Create and configure GTM script element
      const existingScript = doc.getElementsByTagName('script')[0];
      const gtmScript = doc.createElement('script');
      const dataLayerParam = dataLayerName !== 'dataLayer' ? '&l=' + encodeURIComponent(dataLayerName) : '';

      gtmScript.async = true;
      gtmScript.src = `${this.gtmScriptUrl}?id=${encodeURIComponent(gtmId)}${dataLayerParam}`;

      // Insert script into DOM
      if (existingScript?.parentNode) {
        existingScript.parentNode.insertBefore(gtmScript, existingScript);
      } else {
        doc.head.appendChild(gtmScript);
      }

      this.isGtmLoaded = true;
    }
  }

  /**
   * Add GTM noscript iframe to body
   * Uses gtmId_ns which can be same or different from gtmId
   */
  addNoScriptIframe(): void {
    if (this.isNoScriptAdded) {
      return;
    }

    const gtmIdNs = this.runtimeEnvService.gtmIdNs;

    if (!gtmIdNs) {
      console.warn(
        `GTM NoScript ID not configured for ${this.runtimeEnvService.environmentName} environment`
      );
      return;
    }

    // Validate GTM ID format to prevent XSS
    if (!this.isValidGtmId(gtmIdNs)) {
      console.error('Invalid GTM NoScript ID format. Expected format: GTM-XXXXXXXX');
      return;
    }

    console.log(
      `Adding GTM noscript iframe for ${this.runtimeEnvService.environmentName} environment with ID: ${gtmIdNs}`
    );

    // Note: Using native DOM API instead of Renderer2 for noscript element
    // Renderer2 doesn't handle noscript elements correctly in production/SSR builds,
    // causing it to render as string instead of DOM element
    const noscript = this.document.createElement('noscript');

    // Create iframe element using native DOM API for consistency
    const iframe = this.document.createElement('iframe');
    iframe.src = `${this.gtmNoScriptUrl}?id=${encodeURIComponent(gtmIdNs)}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';

    noscript.appendChild(iframe);

    // Insert as first child of body (important for GTM noscript)
    const body = this.document.body;
    if (body.firstChild) {
      body.insertBefore(noscript, body.firstChild);
    } else {
      body.appendChild(noscript);
    }

    this.isNoScriptAdded = true;
  }

  removeSpecialCharacters(input: string): string {
    // Define the regular expression to match special characters
    const regex = /[!@#$%^&*(),.?":{}|<>-]/g;
    // Use the replace method to remove the special characters
    const result = input.replace(regex, '');
    return result;
  }

  /**
   * Function - send data to analytics (GAHR1199)
   */
  sendDataToAnalytics(eventData: any) {
    if (this.userStoreService.userState && this.userStoreService.userState.selectedTerritory) {
      if (this.userStoreService.userState.selectedTerritory.groupTypeCode) {
        var group: string | null = null;
        const gahGroups = new GahGroups(); //GAHR1243
        const groupsMap = gahGroups.gahGroupsMap;
        if (
          groupsMap.hasOwnProperty(this.userStoreService.userState.selectedTerritory.groupTypeCode)
        ) {
          group = groupsMap[this.userStoreService.userState.selectedTerritory.groupTypeCode];
          eventData['user_group'] = group;
        }
        if (this.userStoreService.userState.selectedTerritory.territoryName) {
          eventData['user_country'] =
            this.userStoreService.userState.selectedTerritory.territoryName;
        }
      }
    }
    if (this.userStoreService.userState?.gigyauid) {
      eventData['user_type'] = 'External';
      eventData['user_uuid'] = this.userStoreService.userState?.gigyauid;
    } else {
      eventData['user_type'] = 'Internal';
    }
    if (this.window) {
      // const window = this.browserGlobals.windowRef();
      this.window['dataLayer'] = this.window['dataLayer'] || [];
      this.window['dataLayer'].push({ event: eventData.event, page: eventData });
      console.log(eventData);
    } else {
      console.warn('Window not available - analytics event not sent', eventData);
    }
  }

  getEventSource() {
    // const window = this.browserGlobals.windowRef();
    // const reverseDataLayer = window.dataLayer.slice().reverse();
    if (!this.window || !this.window['dataLayer']) {
      return null;
    }
    const reverseDataLayer = [...this.window['dataLayer']].reverse();
    const gahGroups = new GahGroups();
    const gahEvents = gahGroups.gahEvents;
    for (const event of reverseDataLayer) {
      if (event.event && gahEvents.includes(event.event)) {
        return event.page.page_name;
      }
    }

    return null; // Or handle the case when no event from the array is found
  }
}